import { setJobsSearchShow } from '../../../../../store/workspace/slice';
import { getJobsThunk } from '../../../../../store/workspace/thunks';
import { PostedJob } from '../../../../../store/workspace/types';
import { ListItem } from '../../../../../store/general/types';
import DropDown from '../../../../../components/DropDown';
import { useDispatch } from '../../../../../store/store';
import JobCard from '../../../../../components/JobCard';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import JobsSkeleton from '../JobsSkeleton';
import styles from './style.module.scss';
import {
    ResetIcon,
    SearchIcon,
    XCloseIcon,
    InformationIcon,
} from '../../../../../assets/icons';

interface Props {
    jobs: PostedJob[];
    searchShow: boolean;
    fetchingJobs: boolean;
    postNewJob: () => void;
    fetchingProfile: boolean;
};

type Tab = 'Posted' | 'Unposted';

const postDateSortList = [
    {
        id: 'asc',
        title: 'Newest',
    },
    {
        id: 'desc',
        title: 'Oldest',
    },
];

function JobsPanel(props: Props) {
    const {
        jobs,
        searchShow,
        postNewJob,
        fetchingJobs,
        fetchingProfile,
    } = props;

    const dispatch = useDispatch();
    const isLongList = jobs.length > 5;
    const [tab, setTab] = useState<Tab>('Posted');
    const [citySearch, setCitySearch] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [titleSearch, setTitleSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const postedJobs = jobs.filter(i => i.status === 'posted');
    const unpostedJobs = jobs.filter(i => i.status === 'unposted');
    const [postDateSort, setPostDateSort] = useState<ListItem>(postDateSortList[0]);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'Posted' || tab === 'Unposted') {
            setTab(tab as Tab);
        } else {
            setTab('Posted');
        }
    }, [searchParams]);

    const handleTabChange = (tab: Tab) => {
        setTab(tab);
        setSearchParams({ tab });
    };

    const handleSearch = () => {
        dispatch(getJobsThunk({
            search: titleSearch.trim(),
            city: citySearch.trim(),
            sort: postDateSort.id,
        }));
        setHasSearched(true);
    };

    const handleResetSearch = () => {
        setTitleSearch('');
        setCitySearch('');
        setPostDateSort(postDateSortList[0]);
        dispatch(getJobsThunk({}));
        setHasSearched(false);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.tabs}>
                <div
                    className={styles.tabs_item}
                    data-is-active={tab === 'Posted'}
                    onClick={() => handleTabChange('Posted')}
                >
                    <span>Posted jobs ({postedJobs.length})</span>
                </div>
                <div
                    className={styles.tabs_item}
                    data-is-active={tab === 'Unposted'}
                    onClick={() => handleTabChange('Unposted')}
                >
                    <span>Unposted jobs ({unpostedJobs.length})</span>
                </div>
            </div>

            {searchShow && <div className={styles.search}>
                <div className={styles.search_header}>
                    {hasSearched && <ResetIcon
                        width={20}
                        height={20}
                        onClick={handleResetSearch}
                    />}
                    <XCloseIcon
                        stroke='#2948A7'
                        onClick={() => dispatch(setJobsSearchShow(false))}
                    />
                </div>
                <div className={styles.search_form}>
                    <Input
                        focused
                        type='text'
                        label='Title'
                        value={titleSearch}
                        onChange={setTitleSearch}
                        placeholder='Search by title'
                    />
                    <Input
                        type='text'
                        label='City'
                        value={citySearch}
                        onChange={setCitySearch}
                        placeholder='Search by city'
                    />
                    <DropDown
                        idKey='id'
                        titleKey='title'
                        label='Post date sort'
                        setItem={setPostDateSort}
                        selectedItem={postDateSort}
                        dropDownList={postDateSortList}
                    />
                    <Button
                        text='Search'
                        type='Outline-Primary'
                        loading={fetchingJobs}
                        onClick={handleSearch}
                        className={styles.search_form_button}
                    />
                </div>
            </div>}

            <div className={styles.head}>
                <div className={styles.head_title}>
                    <span className={styles.head_title_text}>Jobs</span>
                    <div className={styles.head_title_info}>
                        <InformationIcon />
                        <div className={styles.head_title_info_tooltip}>
                            By clicking “+ Create Job Post”, you can generate AI-powered
                            assessments and exams for your open position.<br />
                            Once the job post is saved under the <b>Unposted jobs</b> tab,
                            you can review all exam questions and make any necessary changes.
                            When you’re ready, click <b>Posted jobs</b> to make the job post live.
                        </div>
                    </div>
                </div>
                <div className={styles.head_actions}>
                    {!searchShow && <div className={styles.head_actions_search}>
                        <SearchIcon onClick={() => dispatch(setJobsSearchShow(true))} />
                        {hasSearched && <span />}
                    </div>}
                    <Button
                        type='Primary'
                        text='+ Create Job Post'
                        onClick={postNewJob}
                        loading={fetchingProfile}
                        className={styles.head_actions_CTA}
                    />
                </div>
            </div>

            <div className={styles.jobs}>
                {fetchingJobs && <JobsSkeleton />}

                {!fetchingJobs && tab === 'Posted' &&
                    (postedJobs.length > 0 ? postedJobs.map(job => <JobCard detail={job} />) :
                        <div className={styles.empty}><span>{hasSearched ? 'No results found.' : 'No posted jobs.'}</span></div>)}

                {!fetchingJobs && tab === 'Unposted' && (unpostedJobs.length > 0 ?
                    unpostedJobs.map(job => <JobCard detail={job} />) :
                    <div className={styles.empty}><span>{hasSearched ? 'No results found.' : 'No unposted jobs.'}</span></div>)}
            </div>
            {/* {isLongList &&
                (<span
                    className={styles.show_more}
                    onClick={() => navigate('history')}
                >
                    Show all items
                </span>)} */}
        </div>
    )
};

export default JobsPanel;
